  import { React, useNavigate, useState, dash, CssBaseline, TextField, Typography, Grid2, Box, rsk_logo, Password_Regex, useEffect, isTokenValid } from '../utils/globalImports';
  import { CustomButton, Footer, Toaster, InputAdornment, IconButton, VisibilityOff, Visibility } from '../utils/globalImports';
  import useApi from '../hooks/useApi';

  const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const { execute, loading, error } = useApi();
    const [openToaster, setOpenToaster] = useState(false); // State for the toaster
    const [isResetSuccessful, setIsResetSuccessful] = useState(false); // State to disable the button after success
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const email = localStorage.getItem('email');
      useEffect(() => {
        const resetPasswordFlag = localStorage.getItem("resetPasswordFlag");
        if (!resetPasswordFlag || resetPasswordFlag !== "true") {
          navigate("/login");
        }
      }, []);
          
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
    
      if (!email) {
        setPasswordError('Email not found. Please restart the reset process.');
        return;
      }
    
      if (!validateFields(password, confirmPassword)) {
        return;
      }
    
      setSuccessMessage('');
      setPasswordError('');
      setConfirmPasswordError('');
      setSuccessMessage('');
      try {
        const response = await execute('post', 'Auth/reset-password', { email, newPassword: password });
        
        if (response.data) {
          localStorage.removeItem('email');
          localStorage.removeItem('resetPasswordFlag'); // Clear the reset password flag
          setSuccessMessage('Password reset successfully!');
          setIsResetSuccessful(true); // Set to true after success
          setOpenToaster(true); // Show toaster when password is reset
          setTimeout(async() => {
            setOpenToaster(false); // Hide toaster after 3 seconds
           if (isTokenValid()) {
              try {
                await execute('post', '/Auth/logout'); 
              } catch (logoutError) {
                console.error('Logout failed:', logoutError);
              }
            }
            navigate('/login'); // Navigate to login after the toaster disappears
          }, 3000);
        } else if (response.error) {
          if (response.error.includes("The new password cannot be the same as your current password")) {
            setPasswordError("The new password cannot be the same as your current password.");
          } else {
            setPasswordError(response.error || 'Something went wrong. Please try again.');
          }
        }
      } catch (err) {
        console.error("Error in handleSubmit:", err);
        setPasswordError("Network error or server issue.");
      }
    };

    const validateFields = (password: string, confirmPassword: string) => {
      let isValid = true;

    
      if (!password || !Password_Regex.test(password)) {
        setPasswordError(
          'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.'
        );
        isValid = false;
      } else {
        setPasswordError('');
      }
      
      if (!confirmPassword) {
        setConfirmPasswordError('Please confirm your password.');
        isValid = false;
      } else if (password !== confirmPassword) {
        setConfirmPasswordError('Passwords do not match.');
        isValid = false;
      } else {
        setConfirmPasswordError('');
      }
    
      return isValid;
    };

    return (
      <>
        <CssBaseline />
        <Grid2 container className="login-container">
          {/* Left Section */}
          <Grid2 className="left-section">
            <Box className="header">
              <Typography component="h1" variant="h5" className="title">
                Reset Password
              </Typography>
              <img src={dash} alt="Dash" className="dash" />
            </Box>
            <Typography className="subheader">Enter your new password</Typography>
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Typography className="input-label">New Password</Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!passwordError}
                className="text-field"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {passwordError && (
                <Typography className="validation-message" role="alert" color="error">
                  {passwordError}
                </Typography>
              )}

              <Typography className="input-label">Confirm Password</Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!confirmPasswordError}
                className="text-field"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle confirm password visibility"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {confirmPasswordError && (
                <Typography className="validation-message" role="alert" color="error">
                  {confirmPasswordError}
                </Typography>
              )}

              {successMessage && (
                <Typography className="success-message" role="alert" color="primary">
                  {successMessage}
                </Typography>
              )}
              {error && (
                <Typography className="validation-message" role="alert" color="error">
                  {error}
                </Typography>
              )}

              <CustomButton 
                type="submit" 
                capsule 
                className="login-button" 
                disabled={loading || isResetSuccessful} // Disable button if reset is successful
              >
                {loading ? 'Resetting...' : isResetSuccessful ? 'Password Reset Successfully' : 'RESET PASSWORD'}
              </CustomButton>
            </Box>
          </Grid2>

          {/* Right Section */}
          <Grid2 className="right-section">
            <img src={rsk_logo} alt="Logo" className="logo" />
            <Typography className="title">Start managing your finance faster and better</Typography>
          </Grid2>
        </Grid2>
        <Footer />

        {/* Toaster Notification */}
        <Toaster 
          open={openToaster} 
          message="Password reset successfully!" 
          type="success" 
          onClose={() => setOpenToaster(false)} 
        />
      </>
    );
  };

  export default ResetPassword;